"use client";
// catmint/error — Error boundary utilities
import { Component } from "react";
// Re-export ServerFnError for client-side server function error handling
export { ServerFnError } from "./server-fn-error.js";
/**
 * React error boundary component that catches errors in its child tree.
 *
 * Error boundaries must be class components — this is a React requirement
 * because only class components can implement getDerivedStateFromError
 * and componentDidCatch lifecycle methods.
 *
 * @example
 * ```tsx
 * import { ErrorBoundary } from 'catmint/error'
 *
 * <ErrorBoundary fallback={({ error, reset }) => (
 *   <div>
 *     <p>Error: {error.message}</p>
 *     <button onClick={reset}>Retry</button>
 *   </div>
 * )}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    /**
     * Update state when an error is thrown in a descendant component.
     * This lifecycle is invoked after an error has been thrown by a descendant.
     */
    static getDerivedStateFromError(error) {
        // Normalize the error to an Error instance
        const normalizedError = error instanceof Error
            ? error
            : new Error(typeof error === "string" ? error : "An unknown error occurred");
        return { error: normalizedError };
    }
    /**
     * Log error information for debugging.
     * This lifecycle is invoked after an error has been thrown by a descendant.
     */
    componentDidCatch(error, errorInfo) {
        // Log the error with component stack trace for debugging
        if (typeof console !== "undefined" && console.error) {
            console.error("[catmint/error] ErrorBoundary caught an error:", error);
            if (errorInfo.componentStack) {
                console.error("[catmint/error] Component stack:", errorInfo.componentStack);
            }
        }
    }
    /**
     * Reset the error state, causing the children to re-render.
     * This is passed to the fallback component so users can implement retry logic.
     */
    handleReset = () => {
        this.setState({ error: null });
    };
    render() {
        const { error } = this.state;
        const { children, fallback } = this.props;
        if (error !== null) {
            return fallback({
                error,
                reset: this.handleReset,
            });
        }
        return children;
    }
}
//# sourceMappingURL=index.js.map